import { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Paper, LinearProgress, Alert, IconButton,
  Autocomplete, TextField, Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as RemoveIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useUploadDocumentMutation } from '../../../../../services/MedNecService';
import { DOCUMENT_TYPES, TYPE_COLORS } from './documentTypes';

const ACCEPTED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg,.tiff,.tif,.doc,.docx';
const MAX_FILE_SIZE_MB = 50;

interface PendingFile {
  id: string;
  file: File;
  documentTypes: string[];
}

interface DocumentUploadZoneProps {
  caseId: string;
}

const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({ caseId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  const [uploadDocument] = useUploadDocumentMutation();

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `${file.name}: exceeds ${MAX_FILE_SIZE_MB}MB limit`;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const allowed = ACCEPTED_EXTENSIONS.replace(/\./g, '').split(',');
    if (!allowed.includes(ext)) {
      return `${file.name}: unsupported file type .${ext}`;
    }
    return null;
  }, []);

  const addFiles = useCallback((files: FileList) => {
    const errors: string[] = [];
    const newEntries: PendingFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        newEntries.push({ id: `${Date.now()}-${i}`, file, documentTypes: [] });
      }
    }

    if (errors.length > 0) setError(errors.join('; '));
    else setError(null);

    if (newEntries.length > 0) {
      setPendingFiles((prev) => [...prev, ...newEntries]);
    }
  }, [validateFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFiles]);

  const updateDocumentTypes = useCallback((id: string, types: string[]) => {
    setPendingFiles((prev) => prev.map((pf) => pf.id === id ? { ...pf, documentTypes: types } : pf));
  }, []);

  const removeFile = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((pf) => pf.id !== id));
  }, []);

  const allTypesAssigned = pendingFiles.length > 0 && pendingFiles.every((pf) => pf.documentTypes.length > 0);
  const isUploading = uploadProgress !== null;

  const handleUploadAll = useCallback(async () => {
    if (!allTypesAssigned) return;

    setError(null);
    const total = pendingFiles.length;
    const failed: string[] = [];

    for (let i = 0; i < total; i++) {
      setUploadProgress({ current: i + 1, total });
      const pf = pendingFiles[i];
      try {
        await uploadDocument({
          caseId,
          documentTypes: pf.documentTypes,
          file: pf.file,
        }).unwrap();
      } catch {
        failed.push(pf.file.name);
      }
    }

    setUploadProgress(null);
    setPendingFiles((prev) => prev.filter((pf) => failed.includes(pf.file.name)));

    if (failed.length > 0) {
      setError(`Failed to upload: ${failed.join(', ')}`);
    }
  }, [allTypesAssigned, pendingFiles, caseId, uploadDocument]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: dragOver ? '2px dashed #3b82f6' : '2px dashed #cbd5e1',
        borderRadius: 2,
        backgroundColor: dragOver ? '#eff6ff' : '#f8fafc',
        transition: 'all 0.2s ease',
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <UploadIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>
          Drop scheduling packet documents here
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          PDF, images, or Word documents up to {MAX_FILE_SIZE_MB}MB &middot; Multiple files supported
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: pendingFiles.length > 0 ? 2 : 0 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569' }}
        >
          Browse Files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleInputChange}
          multiple
          hidden
        />

        {pendingFiles.length > 0 && (
          <Button
            variant="contained"
            size="small"
            disabled={!allTypesAssigned || isUploading}
            onClick={handleUploadAll}
            sx={{
              textTransform: 'none', fontWeight: 600, ml: 'auto',
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' },
            }}
          >
            {isUploading
              ? `Uploading ${uploadProgress!.current} of ${uploadProgress!.total}…`
              : `Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}`}
          </Button>
        )}
      </Box>

      {/* Pending file queue */}
      {pendingFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {pendingFiles.map((pf) => (
            <Box
              key={pf.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 1.5,
              }}
            >
              <FileIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pf.file.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {(pf.file.size / 1024).toFixed(0)} KB
                </Typography>
              </Box>
              <Autocomplete
                multiple
                options={[...DOCUMENT_TYPES]}
                value={pf.documentTypes}
                onChange={(_, newValue) => updateDocumentTypes(pf.id, newValue)}
                disabled={isUploading}
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        fontWeight: 600, fontSize: '0.7rem',
                        backgroundColor: `${TYPE_COLORS[option] ?? '#64748b'}15`,
                        color: TYPE_COLORS[option] ?? '#64748b',
                        border: `1px solid ${TYPE_COLORS[option] ?? '#64748b'}40`,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder={pf.documentTypes.length === 0 ? 'Document Types' : ''} />
                )}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <IconButton size="small" onClick={() => removeFile(pf.id)} disabled={isUploading} sx={{ color: '#94a3b8' }}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {isUploading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default DocumentUploadZone;
